// src/pages/Tab3.tsx
// Profile page with centered display name and admin controls

import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonButtons,
  IonList,
  IonItem,
  IonLabel,
  IonAlert,
} from '@ionic/react';
import {
  pencilOutline,
  locationOutline,
  personOutline,
  shieldOutline,
  alertCircleOutline,
  logOutOutline,
  settingsOutline,
  logoDiscord,
  logoInstagram,
  linkOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { AvatarPreview } from '../components/avatar/AvatarPreview';
import { supabase } from '../services/supabase';
import { useState, useEffect } from 'react';
import './Tab3.css';

const Tab3: React.FC = () => {
  const history = useHistory();
  const { profile, loading } = useProfile();
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (profile) {
      setIsUserAdmin(profile.is_admin === true);
    }
  }, [profile]);

  const handleEditProfile = () => {
    history.push('/profile/edit');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    history.push('/login');
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Profile</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!profile) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Profile</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText>
            <p>No profile found. Please log in.</p>
          </IonText>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Profile</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Profile Card */}
        <IonCard style={{ margin: '16px' }}>
          <IonCardContent>
            {/* Avatar */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              {profile.avatar && (
                <AvatarPreview
                  avatarData={profile.avatar}
                  size={150}
                  userId={profile.id}
                  isAdmin={profile.is_admin || false}
                />
              )}
            </div>

            {/* Display Name - CENTERED */}
            <IonText>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                margin: '8px 0',
                textAlign: 'center'
              }}>
                {profile.display_name || profile.username}
              </h1>
            </IonText>

            {/* Username */}
            <IonText color="medium">
              <p style={{
                fontSize: '16px',
                margin: '4px 0',
                textAlign: 'center'
              }}>
                @{profile.username}
              </p>
            </IonText>
          </IonCardContent>
        </IonCard>

        {/* About Me Section */}
        {profile.bio && (
          <IonCard style={{ margin: '16px' }}>
            <IonCardContent>
              <IonText>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  About Me
                </h3>
                <p style={{ lineHeight: '1.6', color: 'var(--ion-text-color)' }}>
                  {profile.bio}
                </p>
              </IonText>
            </IonCardContent>
          </IonCard>
        )}

        {/* Profile Details */}
        <IonList style={{ margin: '16px', borderRadius: '8px', overflow: 'hidden' }}>
          {profile.pronouns && (
            <IonItem>
              <IonIcon icon={personOutline} slot="start" />
              <IonLabel>
                <h3>Pronouns</h3>
                <p>{profile.pronouns}</p>
              </IonLabel>
            </IonItem>
          )}

          {profile.location_city && (
            <IonItem>
              <IonIcon icon={locationOutline} slot="start" />
              <IonLabel>
                <h3>Location</h3>
                <p>{profile.location_city}</p>
              </IonLabel>
            </IonItem>
          )}
        </IonList>

        {/* Action Buttons */}
        <div style={{ padding: '16px', paddingBottom: '80px' }} className="profile-actions">
          <IonButton
            expand="block"
            onClick={handleEditProfile}
            size="large"
            style={{ marginBottom: '12px' }}
            className="profile-edit-btn"
          >
            <IonIcon icon={pencilOutline} slot="start" />
            Edit Profile
          </IonButton>

          <IonButton
            expand="block"
            fill="outline"
            onClick={() => history.push('/profile/settings')}
            size="large"
            style={{ marginBottom: '12px' }}
            className="profile-settings-btn"
          >
            <IonIcon icon={settingsOutline} slot="start" />
            Account Settings
          </IonButton>

          <IonButton
            expand="block"
            onClick={handleLogout}
            size="large"
            style={{ marginBottom: '12px' }}
            className="profile-logout-btn"
          >
            <IonIcon icon={logOutOutline} slot="start" />
            Logout
          </IonButton>

          {/* About & Legal Button */}
          <IonButton
            expand="block"
            fill="outline"
            size="large"
            onClick={() => setShowAboutModal(true)}
            style={{ marginBottom: '12px' }}
            className="profile-about-btn"
          >
            About & Legal
          </IonButton>

          {/* Admin button at the very bottom */}
          {isUserAdmin && (
            <IonButton
              expand="block"
              fill="outline"
              onClick={() => history.push('/admin/reports')}
              size="large"
              style={{ marginTop: '20px' }}
              className="profile-admin-btn"
            >
              <IonIcon icon={alertCircleOutline} slot="start" />
              Admin: Review Reports
            </IonButton>
          )}
        </div>

        {/* About/Legal Modal */}
        <IonAlert
          isOpen={showAboutModal}
          onDidDismiss={() => setShowAboutModal(false)}
          header="About Dice Cat"
          subHeader="Version 1.0.0"
          message="© 2025 Dice Cat. All rights reserved."
          buttons={[
            {
              text: 'Join our Discord',
              handler: () => {
                window.open('https://discord.gg/9PV7deF4Eu', '_blank');
                return false; // Keep modal open
              }
            },
            {
              text: 'Follow on Instagram',
              handler: () => {
                window.open('https://www.instagram.com/dicecatapp/', '_blank');
                return false;
              }
            },
            {
              text: 'Follow on Bluesky',
              handler: () => {
                window.open('https://bsky.app/profile/dice-cat-app.bsky.social', '_blank');
                return false;
              }
            },
            {
              text: 'Privacy Policy',
              handler: () => {
                window.open('https://dice-cat.com/privacy.html', '_blank');
                return false;
              }
            },
            {
              text: 'Terms of Service',
              handler: () => {
                window.open('https://dice-cat.com/terms.html', '_blank');
                return false;
              }
            },
            {
              text: 'Child Safety',
              handler: () => {
                window.open('https://dice-cat.com/child-safety.html', '_blank');
                return false;
              }
            },
            {
              text: 'Contact Support',
              handler: () => {
                window.open('mailto:carma@dice-cat.com', '_blank');
                return false;
              }
            },
            {
              text: 'Close',
              role: 'cancel'
            }
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Tab3;